import type React from 'react';
import { useCallback, useMemo } from 'react';

import { useNavigate } from '@change-corgi/core/react/router';

import { useLoginState } from 'src/app/shared/hooks/session';

import type { SuggestedPetition } from '../../../../../../../../shared/api';
import { useSuggestedPetitionsTracking } from '../../../../../../../../shared/hooks/useSuggestedPetitionsTracking';

import { useSignSuggestedPetition } from './useSignSuggestedPetition';

type Result = ModelHookResult<
	{
		canOneClickSign: boolean;
	},
	{
		/**
		 * This handler is be attached to an anchor in the DS1 PetitionCard.
		 *
		 * For guests this does not sign the petition. The default link behavior handles navigation.
		 *
		 * For non-guests, the default navigation is prevented. Instead it waits for a request to sign to complete, then
		 * manually navigates.
		 */
		signLinkMaybeSignOnClick: (event: React.MouseEvent) => void;
		/**
		 * **Only use this handler if canOneClickSign is true.**
		 *
		 * This handler is attached to the button in the DS2 PetitionCard.
		 *
		 * For non-guests, the special click-then-navigate action is handled by the button.
		 *
		 * For guests, this handler is not used at all. A link is rendered for normal navigation.
		 */
		signLinkSignNavigateOnClick: () => void;
		/**
		 * Just tracks the link click.
		 */
		signLinkTrackOnClick: () => void;
	}
>;

type Options = {
	parentPetitionId?: string;
	petition: SuggestedPetition;
	gridPosition: number;
};

export function useSignOnClick({ petition, parentPetitionId, gridPosition }: Options): Result {
	const signPetition = useSignSuggestedPetition({ parentPetitionId, petition, gridPosition });
	const {
		actions: { trackClick },
	} = useSuggestedPetitionsTracking({
		parentPetitionId,
		petitionStatus: 'open',
	});

	const loginState = useLoginState();
	const canOneClickSign = loginState !== 'GUEST';
	const navigate = useNavigate();

	const signPetitionAndNavigate = useCallback(async () => {
		const result = await signPetition();
		navigate(result.redirectUrl);
	}, [navigate, signPetition]);

	const signLinkMaybeSignOnClick = useCallback(
		(event: React.MouseEvent) => {
			// Always track the sign link click.
			trackClick('sign', petition, gridPosition);

			// Outside of the one-click-sign flow the default link behavior will trigger navigation.
			if (!canOneClickSign) {
				return;
			}

			// In the one-click-sign flow we prevent the the default link behavior. The async call below handles it
			// instead. Note: the user may bypass this click handler in other ways, like copy-pasting the link or
			// opening it in a new tab.
			event.preventDefault();

			void signPetitionAndNavigate();
		},
		[canOneClickSign, gridPosition, petition, signPetitionAndNavigate, trackClick],
	);

	const signLinkSignNavigateOnClick = useCallback(() => {
		// Always track the sign link click.
		trackClick('sign', petition, gridPosition);

		void signPetitionAndNavigate();
	}, [gridPosition, petition, signPetitionAndNavigate, trackClick]);

	const signLinkTrackOnClick = useCallback(() => {
		// Just track the sign link click.
		trackClick('sign', petition, gridPosition);
	}, [gridPosition, petition, trackClick]);

	return useMemo(
		() => ({
			data: { canOneClickSign },
			actions: {
				signLinkMaybeSignOnClick,
				signLinkSignNavigateOnClick,
				signLinkTrackOnClick,
			},
		}),
		[canOneClickSign, signLinkMaybeSignOnClick, signLinkSignNavigateOnClick, signLinkTrackOnClick],
	);
}
